import { useEffect } from 'react';
import { useBroadcast } from '../../common/broadcast-channel/use-broadcast';
import { MarkConversationAsRead, UpdateBookmark } from '../../common/broadcast-channel/types';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import { useMessagingStore } from '../hooks/use-messaging-store';
import { useMessagingStream } from '../hooks/use-messaging-stream';
import { useUpdateBookmarkMessageHandler } from '../hooks/message-handlers/use-update-bookmark-message-handler';
import { useMessagingBootstrap } from '../hooks/resource/use-messaging-bootstrap';
import { useConversationMessages } from '../hooks/resource/use-conversation-messages';
import { useBookmarkedConversationMessages } from '../hooks/resource/use-bookmarked-conversation-messages';
import { observer } from 'mobx-react';
import { useSwitchFilterAfterIdentityChange } from '../hooks/use-switch-filter-after-identity-change';
import { useShowNoConversationSelected } from '../hooks/use-show-no-conversation-selected';

// This component fetches and receives data for the messaging store.
//
// It does the following:
//
// 1. loads the participant identities, conversation and bookmarked conversation data when required
// 2. listens to messaging stream and triggers updates in messaging store based on nudges
// 3. listens for broadcast and updates messaging store based on messages
//
// Note that while this component populates the messaging store it is a child of App to avoid App re-renders. However
// the messaging store is a parent of App so child components within App can access the messaging store, and the data
// persists while App is mounted.
//
const DataProcessor = observer(() => {
    const store = useMessagingStore();

    const messagingBootstrapQuery = useMessagingBootstrap(store);

    useEffect(() => {
        if (messagingBootstrapQuery.data && !messagingBootstrapQuery.isPreviousData) {
            const {
                bookmarkedConversations,
                conversations,
                participantIdentities,
            } = messagingBootstrapQuery.data;

            store.setActiveParticipantIdentities(participantIdentities);
            store.setConversations(conversations);
            store.setBookmarkedConversations(bookmarkedConversations);
        }
    }, [store, messagingBootstrapQuery.data, messagingBootstrapQuery.isPreviousData]);

    useSwitchFilterAfterIdentityChange();
    useShowNoConversationSelected();

    const getMessagesForConversationQuery = useConversationMessages(store);
    const getMessagesForBookmarkedConversationQuery = useBookmarkedConversationMessages(store);

    useEffect(() => {
        const selectedConversationMessageList = store.isBookmarksView
            ? getMessagesForBookmarkedConversationQuery.data?.messageList
            : getMessagesForConversationQuery.data?.messageList;
        store.setSelectedConversationMessageList(selectedConversationMessageList);
    }, [
        getMessagesForBookmarkedConversationQuery.data?.messageList,
        getMessagesForConversationQuery.data?.messageList,
        store,
    ]);

    useMessagingStream(store, messagingBootstrapQuery);

    useBroadcast('mark-conversation-as-read', (message: MarkConversationAsRead) => {
        const { conversationId, newestMessageId } = message.payload;
        store.markConversationAsRead(conversationId);
        store.updateGlobalLastReadMessageIdForConversation(conversationId, newestMessageId);
    });

    const updateBookmarkMessageHandler = useUpdateBookmarkMessageHandler(store);

    useBroadcast('update-bookmark', (message: UpdateBookmark) => {
        updateBookmarkMessageHandler(message);
    });

    return null;
});

export const MessagingStoreDataProcessor = () => {
    const { messaging } = useEnabledFeatures();
    if (messaging) {
        return <DataProcessor />;
    } else {
        return null;
    }
};
